import { gql, TypedDocumentNode } from '@apollo/client';

import { DefaultArg, DefaultApiResponse } from '@libs/graphql';
import {
  Transaction,
  TransactionStatusType,
  ExtendedTransactionDetails,
} from '@libs/types';

import { CurrencyType, TransactionType } from 'src/constants';

export type GetRecentTransactionsRes = {
  getRecentTransactions: DefaultApiResponse<Transaction[]>;
};

export type GetRecentTransactionsArgs = {
  skip?: number;
  take?: number;
  assetCodes?: CurrencyType[];
  types?: TransactionType[];
  statuses?: TransactionStatusType[];
  minDate?: string;
  maxDate?: string;
};
export type TransactionsGroupedByDate = {
  createdAt: Date;
  transactions: Transaction[];
};

export type GetUserTransactionsGroupedByDateRes = {
  getUserTransactionsGroupedByDate: DefaultApiResponse<
    TransactionsGroupedByDate[]
  >;
};
export const getUserTransactionsGroupedByDateQuery: TypedDocumentNode<
  GetUserTransactionsGroupedByDateRes,
  DefaultArg<GetRecentTransactionsArgs>
> = gql`
  query GetUserTransactionsGroupedByDate(
    $record: GetRecentTransactionsRecord!
  ) {
    getUserTransactionsGroupedByDate(record: $record) {
      createdAt
      transactions {
        id
        status
        details {
          type
          assetCode
          assetLabel
          assetIconUrl
          fromAsset {
            name
            code
            iconUrl
          }
          toAsset {
            name
            code
            iconUrl
          }
          method

          fee
          feeAssetCode
          fromAmount
          toAmount
          amount
          estimatedAmount
          quoteAmount
          quoteAssetCode
          quoteAssetLabel
          quoteAssetIconUrl

          krakenCost
          krakenFee
          totalKrakenCost
        }
      }
    }
  }
`;

export const getRecentTransactionsQuery: TypedDocumentNode<
  GetRecentTransactionsRes,
  DefaultArg<GetRecentTransactionsArgs>
> = gql`
  query getTransactions($record: GetRecentTransactionsRecord!) {
    getRecentTransactions(record: $record) {
      id
      createdAt
      status
      details {
        type
        assetCode
        assetLabel
        assetIconUrl
        fromAsset {
          name
          code
          iconUrl
        }
        toAsset {
          name
          code
          iconUrl
        }
        method

        fee
        feeAssetCode
        fromAmount
        toAmount
        amount
        estimatedAmount
        quoteAmount
        quoteAssetCode
        quoteAssetLabel
        quoteAssetIconUrl

        krakenCost
        krakenFee
        totalKrakenCost
      }
    }
  }
`;

// Transaction Details
type GetTransactionDetailsType = {
  details: ExtendedTransactionDetails;
};

type GetTransactionDetailsRes = {
  getTransactionDetails: DefaultApiResponse<GetTransactionDetailsType[]>;
};

type GetTransactionDetailsArgs = {
  transactionId: number;
};

export const getTransactionDetailsQuery: TypedDocumentNode<
  GetTransactionDetailsRes,
  DefaultArg<GetTransactionDetailsArgs>
> = gql`
  query getTransactionDetails($record: GetTransactionDetailsRecord!) {
    getTransactionDetails(record: $record) {
      details {
        assetCode
        assetLabel
        assetIconUrl
        status
        internalOperationId
        date
        amount
        type
        destination
        blockchainTransactionId
        network
        rate

        fee
        feeAssetCode
        sourceAddress

        quoteAmount
        quoteAssetCode
        quoteAssetLabel
        quoteAssetIconUrl

        senderBankCode
        senderAccountNumber
        senderCardNumber

        recipientBankCode
        recipientAccountNumber
        recipientCardNumber

        fromAmount
        toAmount
        fromAsset {
          name
          code
          iconUrl
        }
        toAsset {
          name
          code
          iconUrl
        }
      }
    }
  }
`;

// Transactions amount
type getUserTransactionsCountRes = {
  getUserTransactionsCount: DefaultApiResponse<number>;
};

type GetTransactionAmountArgs = {
  assetCodes?: CurrencyType[];
  types?: TransactionType[];
  statuses?: TransactionStatusType[];
  minDate?: string;
  maxDate?: string;
};

export const getUserTransactionsCountQuery: TypedDocumentNode<
  getUserTransactionsCountRes,
  DefaultArg<GetTransactionAmountArgs>
> = gql`
  query getTransactionAmount($record: GetUserTransactionsCountRecord!) {
    getUserTransactionsCount(record: $record)
  }
`;
